// import moment from "moment/moment";

export const truncate = (value, stop, clamp) => {
  if (!value) return "";
  value = value.toString();
  clamp = stop < value.length && clamp !== "" ? clamp || "..." : "";
  return value.slice(0, stop) + clamp;
};

export const mtruncate = (value, stop, clamp = "") => {
  if (!value) return "";
  if (value.length <= stop) {
    return value;
  }
  const clampLen = clamp.length;
  const remainingChars = stop - clampLen;
  const firstHalfLen = Math.ceil(remainingChars / 2);
  const secondHalfLen = Math.floor(remainingChars / 2);
  const firstHalf = value.substr(0, firstHalfLen);
  const secondHalf = value.substr(value.length - secondHalfLen);
  return firstHalf + clamp + secondHalf;
};

export const ltruncate = (value, start, clamp = "") => {
  if (!value) return "";
  value = value.toString();
  return clamp + value.slice(start, value.length);
};

export const ucfirst = (s) => s && s[0].toUpperCase() + s.slice(1);

export const round2Dec = (value) => {
  if (!value) return 0;
  value = Math.round(value * 100) / 100;
  value = value.toLocaleString("en", {
    useGrouping: false,
    minimumFractionDigits: 2,
  });
  return parseFloat(value);
};

export const prettyNbs = (x, sep = " ") => {
  if (!x) return x;
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, sep);
};

export const formatTime = (timeInSeconds) => {
  let hours = Math.floor(timeInSeconds / 3600);
  let minutes = Math.floor((timeInSeconds % 3600) / 60);
  minutes = minutes < 10 ? `0${minutes}` : `${minutes}`;
  let seconds = parseInt(timeInSeconds % 60);
  seconds = seconds < 10 ? `0${seconds}` : `${seconds}`;
  if (hours >= 24) {
    let days = Math.floor(hours / 24);
    days = prettyNbs(days);
    hours = hours % 24;
    hours = hours < 10 ? `0${hours}` : `${hours}`;
    return `${days} days ${hours}h ${minutes}m ${seconds}s`;
  } else if (hours >= 1 && minutes >= 1) {
    return `${hours}h ${minutes}m ${seconds}s`;
  } else if (hours >= 1) {
    return `${hours}h ${minutes}m`;
  } else if (minutes >= 1) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
};

export const formatDaysToTime = (
  daysValue,
  showMinutes = true,
  showSeconds = true
) => {
  const days = Math.floor(daysValue);
  const totalHours = daysValue * 24;
  const hours = Math.floor(totalHours % 24);
  const totalMinutes = (totalHours - Math.floor(totalHours)) * 60;
  const minutes = Math.floor(totalMinutes);
  const seconds = Math.round((totalMinutes - minutes) * 60);

  const formattedDays = String(days).padStart(2, "0");
  const formattedHours = String(hours).padStart(2, "0");
  const formattedMinutes = showMinutes
    ? ` ${String(minutes).padStart(2, "0")}m`
    : "";
  const formattedSeconds = showSeconds
    ? ` ${String(seconds).padStart(2, "0")}s`
    : "";

  return `${formattedDays}d ${formattedHours}h${formattedMinutes}${formattedSeconds}`;
};

const formatters = {};
const DEFAULT_NB_DECIMALS = 2;

export const formatNumber = (
  number,
  {
    compact = false,
    compactLong = false,
    currency = false,
    percent = false,
    nbDecimals = DEFAULT_NB_DECIMALS,
  } = {}
) => {
  const index = [
    Number(compact), // K(ilo), M(ega), G(iga)...
    Number(compactLong),
    Number(currency),
    Number(percent),
    Number(nbDecimals),
  ].join("_");
  if (formatters[index] === undefined) {
    formatters[index] = new Intl.NumberFormat("fr-FR", {
      ...(currency === true ? { style: "currency", currency: "EUR" } : {}),
      maximumFractionDigits: Math.max(nbDecimals, 0),
      ...(compact === true
        ? {
            notation: "compact",
            ...(compactLong ? { compactDisplay: "long" } : {}),
          }
        : {}),
      ...(percent === true ? { style: "percent" } : {}),
    });
  }

  return formatters[index].format(parseFloat(number));
};

export const formatNumberCompact = (number, nbDecimals = DEFAULT_NB_DECIMALS) =>
  formatNumber(number, { compact: true, nbDecimals });

export const formatCurrency = (number, nbDecimals = DEFAULT_NB_DECIMALS) =>
  formatNumber(number, { currency: true, nbDecimals });

export const formatCurrencyCompact = (
  number,
  nbDecimals = DEFAULT_NB_DECIMALS
) => formatNumber(number, { currency: true, compact: true, nbDecimals });

export const formatCurrencyCompactLong = (
  number,
  nbDecimals = DEFAULT_NB_DECIMALS
) =>
  formatNumber(number, {
    currency: true,
    compact: true,
    compactLong: true,
    nbDecimals,
  });

export const formatPercent = (number, nbDecimals = DEFAULT_NB_DECIMALS) =>
  formatNumber(number, { percent: true, nbDecimals });

export const formatVariation = (value, nbDecimals = DEFAULT_NB_DECIMALS) => {
  const number = value >= 1 ? value - 1 : 1 - value;
  const sign = value > 1 ? "+ " : value < 1 ? "- " : "";

  return `${sign}${formatNumber(number, { percent: true, nbDecimals })}`;
};

export const decodeHtmlEntities = (encoded) => {
  const decoder = decoder || document.createElement("div");
  decoder.innerHTML = encoded;

  return decoder.textContent;
};

export const downloadCsvFile = (csvData, filename) => {
  if (csvData.length === 0) {
    console.error("exportCsvData: empty data");
    return;
  }
  const downloadLink = document.createElement("a");
  const blob = new Blob(["\ufeff", csvData]);
  downloadLink.href = URL.createObjectURL(blob);
  downloadLink.download = filename;
  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
};

export const formatBytes = (bytes, decimals = 2) => {
  if (typeof bytes !== "number" || !Number.isFinite(bytes)) {
    return "Invalid Input";
  }
  if (bytes === 0) {
    return "0 Bytes";
  }
  const k = 1024;
  const dm = Math.max(0, Math.floor(decimals));
  const units = ["Bytes", "KB", "MB", "GB", "TB"];
  const isNegative = bytes < 0;
  const absBytes = Math.abs(bytes);
  let i = 0; // Default to Bytes index (covers absBytes < 1 and avoids log issues)
  if (absBytes >= 1) {
    // Only perform log calculation if magnitude is 1 or greater
    i = Math.floor(Math.log(absBytes) / Math.log(k));
  }
  const unitIndex = Math.max(0, Math.min(i, units.length - 1));
  const valueInUnit = absBytes / Math.pow(k, unitIndex);
  const formattedValue = parseFloat(valueInUnit.toFixed(dm));
  const sign = isNegative ? "-" : "";
  return `${sign}${formattedValue} ${units[unitIndex]}`;
};

/**
 * Parse date string as UTC
 * @param {string} dateVal - Date string to parse
 * @returns {Date} Parsed date object
 */
export const parseDateAsUtc = (dateVal) => {
  if (!dateVal || typeof dateVal !== "string") return new Date(NaN);
  let dateString = dateVal.replace(" ", "T");
  if (!dateString.endsWith("Z") && !/[+-]\d{2}:\d{2}$/.test(dateString)) {
    dateString += "Z";
  }
  return new Date(dateString);
};

/**
 * Format date for human-readable display (French locale)
 * @param {string|Date} val - Date string or Date object
 * @param {string} [locale="fr-FR"] - Locale for formatting
 * @returns {string} Formatted date string
 */
export const formatDateHumanReadable = (val, locale = "fr-FR") => {
  if (!val) return "—";
  const d = parseDateAsUtc(val);
  if (isNaN(d.getTime())) return val;
  return d.toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Format date for display in tables (simpler format)
 * @param {string|Date} val - Date string or Date object
 * @param {string} [locale="fr-FR"] - Locale for formatting
 * @returns {string} Formatted date string
 */
export const formatDateSimple = (val, locale = "fr-FR") => {
  if (!val) return "—";
  const d = parseDateAsUtc(val);
  if (isNaN(d.getTime())) return val;
  return d.toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

/**
 * Format date and time for display
 * @param {string|Date} val - Date string or Date object
 * @param {string} [locale="fr-FR"] - Locale for formatting
 * @param {object} [options] - Optional display settings
 * @param {string} [options.timeZone] - IANA timezone used for display
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (
  val,
  locale = "fr-FR",
  { timeZone } = {}
) => {
  if (!val) return "—";
  const d = parseDateAsUtc(val);
  if (isNaN(d.getTime())) return val;
  return d.toLocaleString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    ...(timeZone ? { timeZone } : {}),
  });
};
export const addTimeLeadingZero = (value) => {
  const leaded = "0" + value;
  return leaded.substring(leaded.length - 2);
};

const NRD_SKIP_SEGMENTS = new Set([
  "all",
  "all-about",
  "arc",
  "atom",
  "categorie",
  "categories",
  "category",
  "default",
  "dossier",
  "dossiers",
  "feed",
  "feeds",
  "flux",
  "global",
  "headlines",
  "index",
  "outboundfeeds",
  "posts",
  "rss",
  "rubrique",
  "rubriques",
  "section",
  "sections",
  "spip",
  "tag",
  "tags",
  "tags_slug",
  "thema",
  "thematique",
  "topic",
  "topics",
  "une",
  "v1",
  "v2",
  "xml",
]);

const NRD_GENERIC_SEGMENTS = new Set([
  "actu",
  "actualite",
  "actualites",
  "actus",
  "article",
  "articles",
  "info",
  "infos",
  "news",
  "toute-l-actualite",
]);

const normalizeConfigWords = (text) =>
  (text || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const extractSectionFromEntry = (entry) => {
  if (!entry || typeof entry !== "string") return "";
  let parsed;
  try {
    parsed = new URL(entry.includes("://") ? entry : `https://${entry}`);
  } catch {
    return "";
  }
  const host = parsed.hostname.toLowerCase();
  if (host.endsWith("rss.app") || host.endsWith("news.google.com")) {
    return "";
  }
  const segments = [];
  for (const raw of parsed.pathname.split("/")) {
    let seg = "";
    try {
      seg = decodeURIComponent(raw).trim();
    } catch {
      seg = raw.trim();
    }
    if (!seg) continue;
    seg = seg.replace(/\.(?:xml|rss|atom|php|html?|asp1?|aspx|json)$/i, "");
    seg = seg.replace(/([a-zA-Z])[-,]\d{4,}$/, "$1");
    if (/^(?:rss|feed)[-_]+/i.test(seg)) {
      const stripped = seg.replace(/^(?:rss|feed)[-_]+/i, "");
      if (stripped) seg = stripped;
    }
    const low = seg.toLowerCase();
    if (seg.length <= 1 || /^\d+$/.test(seg) || NRD_SKIP_SEGMENTS.has(low)) {
      continue;
    }
    segments.push(seg);
  }
  if (
    !segments.length ||
    segments.every((seg) => NRD_GENERIC_SEGMENTS.has(seg.toLowerCase()))
  ) {
    return "";
  }
  return segments.join("/");
};

const isBareSiteName = (name, entry) => {
  if (!name || !entry || typeof entry !== "string") return false;
  let parsed;
  try {
    parsed = new URL(entry.includes("://") ? entry : `https://${entry}`);
  } catch {
    return false;
  }
  const host = parsed.hostname.toLowerCase().replace(/^www\./, "");
  const stem = host.split(".")[0];
  const normName = normalizeConfigWords(name).replace(/\s+/g, "");
  const normHost = normalizeConfigWords(host).replace(/\s+/g, "");
  const normStem = normalizeConfigWords(stem).replace(/\s+/g, "");
  return Boolean(normName) && (normName === normHost || normName === normStem);
};

export const formatNewsRadarConfigName = (
  config,
  { forceSection = false } = {}
) => {
  if (!config || typeof config !== "object") return "";
  const base = (config.name || "").trim();
  if (!base) return "";
  const sourceName = config.news_radar_source?.name || "";
  const shouldEnrich =
    forceSection ||
    (sourceName === "Website (via RssApp)" &&
      isBareSiteName(base, config.entry));
  if (!shouldEnrich) return base;

  const section = extractSectionFromEntry(config.entry);
  if (!section) return base;

  const normBase = ` ${normalizeConfigWords(base)} `;
  const parts = section.split("/");
  const specificParts = parts.filter(
    (part) => !NRD_GENERIC_SEGMENTS.has(part.toLowerCase())
  );
  const checkParts = specificParts.length ? specificParts : parts;
  const leafPart = checkParts[checkParts.length - 1];
  const leafWords = normalizeConfigWords(leafPart)
    .split(" ")
    .filter((w) => w.length > 2);
  if (leafWords.length && leafWords.every((w) => normBase.includes(` ${w} `))) {
    return base;
  }
  return `${base} - ${section}`;
};

export const formatNewsRadarConfigList = (
  configs,
  referenceConfigs = configs
) => {
  if (!Array.isArray(configs)) return [];
  const pool = Array.isArray(referenceConfigs) ? referenceConfigs : configs;
  const seenIds = new Set();
  const nameCounts = {};
  for (const config of pool) {
    if (!config || typeof config !== "object") continue;
    if (config.id !== undefined && seenIds.has(config.id)) continue;
    if (config.id !== undefined) seenIds.add(config.id);
    const key = (config.name || "").trim().toLowerCase();
    if (key) {
      nameCounts[key] = (nameCounts[key] || 0) + 1;
    }
  }
  return configs.map((config) => {
    const key = (config?.name || "").trim().toLowerCase();
    const forceSection = (nameCounts[key] || 0) > 1;
    return {
      ...config,
      name: formatNewsRadarConfigName(config, { forceSection }),
    };
  });
};
